import { useState } from 'react';
import { ShieldCheck, FileText, Flame, Wrench, ArrowRight, Mail, Lock, User as UserIcon, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AUTH } from '@/constants/testIds';
import { apiAuth } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

function formatErr(detail) {
  if (!detail) return 'Erreur inconnue';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' • ');
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');

  const handleGoogleLogin = () => {
    setLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = mode === 'login'
        ? await apiAuth.loginPassword({ email, password })
        : await apiAuth.register({ email, password, full_name: fullName });
      setUser(data.user);
      toast.success(mode === 'login' ? 'Connexion réussie' : 'Compte créé avec succès');
      navigate('/dashboard');
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#09090B] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-50"></div>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1566226196556-ef949ce5f1a3?auto=format&fit=crop&w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(100%)',
          }}
        ></div>
        <div className="relative z-10 flex flex-col justify-between p-16 w-full">
          <div>
            <div className="text-4xl font-display font-black tracking-tighter">LAFARGE</div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-stone-400 mt-1">
              Ciments France
            </div>
            <div className="h-1 w-16 bg-[#DA291C] mt-6"></div>
          </div>

          <div className="space-y-8">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-400 mb-2">
                Plateforme HSE
              </div>
              <h1 className="text-5xl font-display font-black tracking-tighter leading-tight">
                Autorisations<br /> de Travail
              </h1>
              <p className="text-stone-300 mt-6 max-w-md leading-relaxed">
                Gestion centralisée des autorisations de travail, signatures électroniques
                et conformité réglementaire pour l'ensemble des établissements Lafarge Ciments France.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3 max-w-md">
              {[
                { icon: FileText, label: 'Autorisation\nde Travail' },
                { icon: Flame, label: 'Permis\nde Feu' },
                { icon: Wrench, label: 'Permis\nde Levage' },
              ].map((item, idx) => (
                <div key={idx} className="border border-stone-700 p-3 bg-stone-900/50">
                  <item.icon className="w-5 h-5 text-[#DA291C]" strokeWidth={2.5} />
                  <div className="text-[10px] font-bold uppercase tracking-wider mt-2 whitespace-pre-line">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[10px] text-stone-500 uppercase tracking-wider">
            © 2026 Lafarge Ciments France — Direction HSE
          </div>
        </div>
      </div>

      {/* Right: Login panel */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-12">
            <div className="text-3xl font-display font-black tracking-tighter text-[#09090B]">LAFARGE</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 mt-0.5">
              Ciments France
            </div>
            <div className="h-1 w-12 bg-[#DA291C] mt-4"></div>
          </div>

          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-3">
            Connexion sécurisée
          </div>
          <h2 className="text-4xl font-display font-black tracking-tighter text-[#09090B] mb-4">
            {mode === 'login' ? 'Bienvenue.' : 'Créer un compte.'}
          </h2>

          {/* Tab switcher */}
          <div className="flex border-b-2 border-stone-200 mb-6">
            {['login', 'register'].map((m) => (
              <button
                key={m}
                data-testid={`login-tab-${m}`}
                onClick={() => { setMode(m); setErr(''); }}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                  mode === m
                    ? 'text-[#09090B] border-b-2 border-[#DA291C] -mb-0.5'
                    : 'text-stone-500 hover:text-[#09090B]'
                }`}
              >
                {m === 'login' ? 'Connexion' : 'Inscription'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="label-industrial">Nom complet</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    type="text"
                    data-testid="login-fullname"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="input-industrial w-full pl-10"
                    placeholder="Prénom Nom"
                    required
                  />
                </div>
              </div>
            )}
            <div>
              <label className="label-industrial">Email professionnel</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-industrial w-full pl-10"
                  placeholder="prenom.nom@lafarge.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="label-industrial">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-industrial w-full pl-10"
                  placeholder={mode === 'register' ? 'Min. 8 caractères, 1 lettre + 1 chiffre' : '••••••••'}
                  required
                  minLength={mode === 'register' ? 8 : undefined}
                />
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="flex items-start gap-2 p-3 bg-red-50 border-l-4 border-[#DA291C] text-xs text-[#DA291C] font-medium">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              data-testid={mode === 'login' ? 'login-submit' : 'register-submit'}
              disabled={loading}
              className="btn-industrial-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? 'Chargement...' : (mode === 'login' ? 'Se connecter' : 'Créer mon compte')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* Separator */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-stone-200"></div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-400">ou</div>
            <div className="flex-1 h-px bg-stone-200"></div>
          </div>

          <button
            data-testid={AUTH.loginButton}
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border-2 border-stone-300 hover:border-[#09090B] text-[#09090B] text-sm font-bold uppercase tracking-wider transition-colors group"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{loading ? 'Redirection...' : 'Continuer avec Google'}</span>
          </button>

          <div className="mt-8 p-4 border border-stone-200 bg-stone-50">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-[#DA291C] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-[#09090B] mb-1">
                  Sécurité & Confidentialité
                </div>
                <div className="text-xs text-stone-600 leading-relaxed">
                  Mots de passe stockés en bcrypt. Sessions chiffrées (cookies httpOnly).
                  Conforme RGPD et politique de sécurité Lafarge.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-[10px] uppercase tracking-wider text-stone-400">
            Version 1.0 MVP — Accès réservé au personnel autorisé
          </div>
        </div>
      </div>
    </div>
  );
}
